import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFieldArray, useForm } from 'react-hook-form';
import { initialPersonalDataStepFormValues } from 'models/forms/applicationForm';
import { FormInput } from 'components/molecules/FormInput';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { yupResolver } from '@hookform/resolvers/yup';
import ApplicationStore from 'state/ApplicationStore';
import { EDictionaryTypes } from 'models/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { useApplyWizard } from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/hooks';
import {
  StyledParagraph,
  StyledSectionTitle,
  StyledStepTitle,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import AddPreviousAddressListItem from './AddPreviousAddressListItem';
import { usePersonalDataStepFormSchema } from './schemaValidations';

interface IPersonalDataStep {
  setNextStep: (arg: number) => void;
}

const PersonalDataStep = ({ setNextStep }: IPersonalDataStep) => {
  const [showPreviousAddresses, setShowPreviousAddresses] = useState<boolean>(false);
  const [showPreviousNames, setShowPreviousNames] = useState<boolean>(false);
  const [showWorkSection, setShowWorkSection] = useState<boolean>(false);
  const { unit, getProperties } = useGlobalProperty();
  const {
    loadDictionaries,
    civilianPrefix,
    gender,
    phoneCode,
    maritalStatus,
    identificationDocumentType,
    nationalityAll,
  } = useApplyWizard();

  useEffect(() => {
    if (!unit) {
      getProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { schema } = usePersonalDataStepFormSchema();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: initialPersonalDataStepFormValues,
    resolver: yupResolver(schema) as any,
  });
  const { fields, append } = useFieldArray({
    control,
    name: 'previousAddressesList',
  });

  const formRef = useRef<HTMLFormElement | null>(null);
  const watchPreviousAddresses = watch('previousAddresses');
  const watchChangedName = watch('changedName');
  const watchIsEmployment = watch('isEmployment');

  (window as any).fireCycleStepOne = () => {
    reset({
      title: '2a9f6f41-7633-4f01-bff8-8f036f2d5726',
      surname: 'Kowalski',
      forename: 'Piotr',
      gender: '83b59a01-bbdf-4fe5-a537-545bb66a3b6b',
      maritalStatus: 'd3f85d2c-8043-4601-85b2-1e6dd96db0cd',
      documentType: '7ff12d53-8ef6-40fd-b956-5ce6e1f04a22',
      documentNumber: '01223343',
      homeAddress: 'Baker Street 100',
      postcode: '00-000',
      city: 'London',
      state: 'London',
      country: 'United Kingdom',
      mobilePrefix: '4338d446-7530-4b37-b6df-5eec2352255c',
      mobilePhoneNumber: '123123123',
      dateOfBirth: '1999-01-01',
      nationality: 'Polish',
      workCity: 'London',
      workState: 'London',
      workCountry: 'United Kingdom',
    });
  };

  useEffect(() => {
    const handler = () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    };
    document.addEventListener('submitFormEvent', handler);
    return () => {
      document.removeEventListener('submitFormEvent', handler);
    };
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.Gender,
        EDictionaryTypes.PhoneCode,
        EDictionaryTypes.MaritalStatus,
        EDictionaryTypes.IdentificationDocumentType,
        EDictionaryTypes.Nationality,
      ]);
    })();
  }, []);

  useEffect(() => {
    const formData = ApplicationStore.getStepData('personalData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ApplicationStore.setStepErrors(0, Object.keys(errors).length > 0);
  }, [errors]);

  useEffect(() => {
    setShowPreviousAddresses(watchPreviousAddresses === '1');
    if (watchPreviousAddresses === '0') {
      setValue('previousAddressesList', [
        {
          previousAddress: '',
          previousPostcode: '',
          prevCity: '',
          prevState: '',
          prevCountry: '',
          prevFromDate: '',
          prevToDate: '',
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPreviousAddresses]);

  useEffect(() => {
    setShowPreviousNames(watchChangedName === '1');
  }, [watchChangedName]);

  useEffect(() => {
    setShowWorkSection(watchIsEmployment === '1');
  }, [watchIsEmployment]);

  const onSubmitForm = (data: any) => {
    ApplicationStore.setStepData('personalData', data);
    ApplicationStore.setStepErrors(0, false);
    setNextStep(1);
  };

  const addPreviousAddress = () => {
    append({
      previousAddress: '',
      previousPostcode: '',
      prevCity: '',
      prevState: '',
      prevCountry: '',
      prevFromDate: '',
      prevToDate: '',
    });
  };

  return (
    <Box>
      <StyledStepTitle variant="h4">Personal Data</StyledStepTitle>
      <StyledParagraph variant="body2">
        Personal data (including sensitive personal data) will be processed on a public
        interest basis, for the purpose of allowing the police to assess the suitability of an
        applicant to be granted a firearm or shotgun certificate, or to assess the applicant’s
        continued suitability to possess a firearm or shotgun while the certificate remains
        valid.
      </StyledParagraph>

      <StyledSectionTitle variant="h5">Personal Information</StyledSectionTitle>

      <form onSubmit={handleSubmit(onSubmitForm)} ref={formRef}>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(civilianPrefix)}
              setValue={setValue}
              name="title"
              label="Title"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="forename"
              label="First Name"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="middleNames"
              label="Middle Name(s)"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="surname"
              label="Surname"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Box>
          <FormRadioGroup
            name="changedName"
            label="Have you legally changed your name?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showPreviousNames && (
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <FormInput
                name="previousSurname"
                label="Previous Surname"
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid item xs={4}>
              <FormInput
                name="previousForenames"
                label="Previous Forenames"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(gender)}
              setValue={setValue}
              name="gender"
              label="Gender"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(maritalStatus)}
              setValue={setValue}
              name="maritalStatus"
              label="Marital Status"
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Identity Document</StyledSectionTitle>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(identificationDocumentType)}
              setValue={setValue}
              name="documentType"
              label="Document Type"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="documentNumber"
              label="Document Number"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Your address</StyledSectionTitle>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="homeAddress"
              label="Home Address"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="postcode"
              label="Postcode"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="city"
              label="City/Town"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="state"
              label="State/Province/County"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="country"
              label="Country"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="typeOfResidence"
              label="Type of Residence"
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="homePhoneNumber"
              label="Home Telephone Number"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormSelect
              options={mapDictionaryToOptionProp(phoneCode)}
              setValue={setValue}
              name="mobilePrefix"
              label="Mobile Prefix"
              required
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormInput
              name="mobilePhoneNumber"
              label="Mobile Phone Number"
              required
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={8}>
            <FormInput
              name="email"
              label="Email Address"
              control={control}
              errors={errors}
              required
            />
          </Grid>

          <Grid item xs={4}>
            <FormInput
              name="height"
              label="Height"
              control={control}
              errors={errors}
              endAdornmentText="cm"
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormInput
              name="placeOfBirth"
              label="Place of Birth"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <FormDatePicker
              name="dateOfBirth"
              openTo="year"
              label="Date of Birth"
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <FormSelect
              name="nationality"
              label="Nationality"
              required
              control={control}
              errors={errors}
              options={mapDictionaryToOptionProp(nationalityAll)}
              setValue={setValue}
            />
          </Grid>
        </Grid>

        <StyledSectionTitle variant="h5">Previous Addresses</StyledSectionTitle>

        <Box>
          <FormRadioGroup
            name="previousAddresses"
            label="Have you moved your home address in the last 5 Years?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showPreviousAddresses && (
          <Box>
            {fields.map((field, index) => (
              <AddPreviousAddressListItem
                key={field.id}
                arrayName="previousAddressesList"
                index={index}
                control={control}
                errors={errors}
                addNew={addPreviousAddress}
                count={fields.length}
              />
            ))}
          </Box>
        )}

        <StyledSectionTitle variant="h5">Work Information</StyledSectionTitle>

        <Box>
          <FormRadioGroup
            name="isEmployment"
            label="Are you currently in employment?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
        </Box>

        {showWorkSection && (
          <>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <FormInput
                  name="occupation"
                  label="Occupation"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={8}>
                <FormInput
                  name="workAddress"
                  label="Work Address"
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workPostcode"
                  label="Work Postcode"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormInput
                  name="workCity"
                  label="City/Town"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workState"
                  label="State/Province"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <FormInput
                  name="workCountry"
                  label="Country"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={4}>
                <FormInput
                  name="workPhoneNumber"
                  label="Work Telephone Number"
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={8}>
                <FormInput
                  name="workEmail"
                  label="Work Email"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </>
        )}
      </form>
    </Box>
  );
};

export default PersonalDataStep;
